/**
 * PlaybackStatusService - Manager'a oynatma durumu bildirimi
 *
 * Desktop player ile aynı formatta playbackStatus mesajları gönderir.
 * Manager cihazlar sayfasında "oynatma" sütununda görünür.
 */

import websocketService from './websocketService';

// Content type - Manager'da ikon belirlemek için kullanılır
export type PlaybackContentType = 'song' | 'ezan' | 'anons';

export interface SongDetails {
  name: string;
  artist: string;
  currentTime: number;
  duration: number;
  type: PlaybackContentType;
}

export interface PlaybackStatusData {
  isPlaying: boolean;
  songId: string;
  songDetails: SongDetails;
}

class PlaybackStatusService {
  private static instance: PlaybackStatusService;
  private updateIntervalId: ReturnType<typeof setInterval> | null = null;
  private lastStatus: PlaybackStatusData | null = null;
  private audioRef: HTMLAudioElement | null = null;

  // Periyodik güncelleme aralığı (ms)
  private readonly UPDATE_INTERVAL = 30000; // 30 saniye

  private constructor() {}

  static getInstance(): PlaybackStatusService {
    if (!PlaybackStatusService.instance) {
      PlaybackStatusService.instance = new PlaybackStatusService();
    }
    return PlaybackStatusService.instance;
  }

  /**
   * Audio element referansını ayarla (periyodik güncelleme için)
   */
  setAudioRef(audio: HTMLAudioElement | null): void {
    this.audioRef = audio;
  }

  /**
   * Şarkı çalmaya başladığında çağır
   */
  sendPlaying(
    songId: string,
    name: string,
    artist: string,
    duration: number,
    type: PlaybackContentType = 'song'
  ): void {
    const status: PlaybackStatusData = {
      isPlaying: true,
      songId,
      songDetails: {
        name,
        artist,
        currentTime: this.audioRef?.currentTime || 0,
        duration,
        type
      }
    };

    this.lastStatus = status;
    this.sendStatus(status);
    this.startPeriodicUpdates();
  }

  /**
   * Mevcut durumu hemen gönder (Manager açıldığında görmesi için)
   */
  sendCurrentStatus(): void {
    if (this.lastStatus && this.audioRef) {
      const status: PlaybackStatusData = {
        ...this.lastStatus,
        isPlaying: !this.audioRef.paused, // Audio'nun gerçek durumunu kullan
        songDetails: {
          ...this.lastStatus.songDetails,
          currentTime: this.audioRef.currentTime || 0
        }
      };
      this.sendStatus(status);
    }
  }

  /**
   * Şarkı duraklatıldığında çağır
   */
  sendPaused(): void {
    if (!this.lastStatus) return;

    const status: PlaybackStatusData = {
      ...this.lastStatus,
      isPlaying: false,
      songDetails: {
        ...this.lastStatus.songDetails,
        currentTime: this.audioRef?.currentTime || 0
      }
    };

    this.sendStatus(status);
    this.stopPeriodicUpdates();
  }

  /**
   * Şarkı devam ettiğinde çağır
   */
  sendResumed(): void {
    if (!this.lastStatus) return;

    const status: PlaybackStatusData = {
      ...this.lastStatus,
      isPlaying: true,
      songDetails: {
        ...this.lastStatus.songDetails,
        currentTime: this.audioRef?.currentTime || 0
      }
    };

    this.lastStatus = status;
    this.sendStatus(status);
    this.startPeriodicUpdates();
  }

  /**
   * Ezan vakti başladığında çağır
   */
  sendEzanStarted(prayerName: string): void {
    const status: PlaybackStatusData = {
      isPlaying: true,
      songId: 'ezan',
      songDetails: {
        name: `${prayerName} Ezanı`,
        artist: 'Ezan Vakti',
        currentTime: 0,
        duration: 0,
        type: 'ezan'
      }
    };

    this.lastStatus = status;
    this.sendStatus(status);
  }

  /**
   * Ezan vakti bittiğinde çağır - önceki duruma dön
   */
  sendEzanEnded(): void {
    // Ezan bitti, müzik devam edecekse sendPlaying tekrar çağrılacak
    this.lastStatus = null;
  }

  /**
   * Anons (kampanya) çalmaya başladığında çağır
   */
  sendAnnouncementPlaying(
    announcementId: string,
    title: string,
    duration: number
  ): void {
    const status: PlaybackStatusData = {
      isPlaying: true,
      songId: announcementId,
      songDetails: {
        name: title,
        artist: 'Anons',
        currentTime: 0,
        duration,
        type: 'anons'
      }
    };

    this.lastStatus = status;
    this.sendStatus(status);
  }

  /**
   * Oynatma tamamen durduğunda çağır
   */
  sendStopped(): void {
    if (!this.lastStatus) return;

    const status: PlaybackStatusData = {
      ...this.lastStatus,
      isPlaying: false
    };

    this.sendStatus(status);
    this.stopPeriodicUpdates();
    this.lastStatus = null;
  }

  /**
   * WebSocket üzerinden status gönder
   */
  private sendStatus(status: PlaybackStatusData): void {
    websocketService.sendMessage({
      type: 'playbackStatus',
      data: status
    });
  }

  /**
   * Periyodik güncelleme başlat (30 saniyede bir)
   */
  private startPeriodicUpdates(): void {
    // Önceki interval'i temizle
    this.stopPeriodicUpdates();

    this.updateIntervalId = setInterval(() => {
      if (this.lastStatus && this.lastStatus.isPlaying && this.audioRef) {
        const updatedStatus: PlaybackStatusData = {
          ...this.lastStatus,
          songDetails: {
            ...this.lastStatus.songDetails,
            currentTime: this.audioRef.currentTime || 0
          }
        };
        this.sendStatus(updatedStatus);
      }
    }, this.UPDATE_INTERVAL);
  }

  /**
   * Periyodik güncellemeyi durdur
   */
  private stopPeriodicUpdates(): void {
    if (this.updateIntervalId) {
      clearInterval(this.updateIntervalId);
      this.updateIntervalId = null;
    }
  }

  /**
   * Temizlik - component unmount'ta çağır
   */
  cleanup(): void {
    this.stopPeriodicUpdates();
    this.lastStatus = null;
    this.audioRef = null;
  }
}

export default PlaybackStatusService.getInstance();
